import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { forumAPI, settingsAPI } from '../../services/api';
import ForumCreateForm from './ForumCreateForm';
import ForumEditForm from './ForumEditForm';
import LoginPromptModal from './LoginPromptModal';
import './ForumList.css';

const ForumList = () => {
  const { user, isAuthenticated } = useAuth();
  
  // Ensure isAuthenticated defaults to false if undefined
  const authenticated = isAuthenticated ?? false;
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingForum, setEditingForum] = useState(null);
  const [deletingForum, setDeletingForum] = useState(null);
  const [deletingAttachment, setDeletingAttachment] = useState(null);
  const [replyingToForum, setReplyingToForum] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [votingReplyId, setVotingReplyId] = useState(null);
  const [votingForumId, setVotingForumId] = useState(null);
  const [globalVotingEnabled, setGlobalVotingEnabled] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptAction, setLoginPromptAction] = useState('continue');
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [expandedReplies, setExpandedReplies] = useState(new Set());

  useEffect(() => {
    fetchForums();
  }, [statusFilter, debouncedSearch]);

  // Load global voting setting once
  useEffect(() => {
    const loadGlobalVoting = async () => {
      try {
        const res = await settingsAPI.getByKey('forum_voting_enabled');
        const enabled = res.success ? String(res.data.content).toLowerCase() === 'true' : true;
        setGlobalVotingEnabled(enabled);
      } catch (e) {
        setGlobalVotingEnabled(true); // default enabled
      }
    };
    loadGlobalVoting();
  }, []);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  const fetchForums = async () => {
    try {
      setLoading(true);
      let params = {};
      
      if (statusFilter === 'all') {
        // By default, only show open and completed forums (hide spam and removed)
        params = { exclude_status: 'spam,removed' };
      } else {
        params = { status: statusFilter };
      }

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      
      const response = await forumAPI.getForums(params);
      setForums(response.data.data);
    } catch (err) {
      setError('Failed to fetch forums');
      console.error('Error fetching forums:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { class: 'status-open', text: 'Open' },
      completed: { class: 'status-completed', text: 'Completed' },
      spam: { class: 'status-spam', text: 'Spam' },
      removed: { class: 'status-removed', text: 'Removed' }
    };
    
    const config = statusConfig[status] || { class: 'status-default', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const MAX_TEXT_LENGTH = 200; // Characters to show before truncating

  const toggleDescription = (forumId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(forumId)) {
        newSet.delete(forumId);
      } else {
        newSet.add(forumId);
      }
      return newSet;
    });
  };

  const toggleReply = (replyId) => {
    setExpandedReplies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(replyId)) {
        newSet.delete(replyId);
      } else {
        newSet.add(replyId);
      }
      return newSet;
    });
  };

  const renderTextWithSeeMore = (text, itemId, isExpanded, onToggle) => {
    if (!text) return null;
    
    const isLong = text.length > MAX_TEXT_LENGTH;
    const displayText = isLong && !isExpanded 
      ? text.substring(0, MAX_TEXT_LENGTH) + '...' 
      : text;

    return (
      <div>
        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayText}</p>
        {isLong && (
          <button 
            onClick={onToggle}
            className="see-more-btn"
            style={{
              background: 'none',
              border: 'none',
              color: '#0073b1',
              cursor: 'pointer',
              padding: '4px 0',
              marginTop: '4px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {isExpanded ? 'See less' : 'See more'}
          </button>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditForum = async (forum) => {
    try {
      // Fetch the full forum data with attachments to ensure URLs are loaded
      const response = await forumAPI.getForum(forum.id);
      if (response.success) {
        setEditingForum(response.data);
      } else {
        console.error('Failed to fetch forum details:', response.message);
        setEditingForum(forum); // Fallback to original forum data
      }
    } catch (err) {
      console.error('Error fetching forum details:', err);
      setEditingForum(forum); // Fallback to original forum data
    }
  };

  const handleDeleteForum = async (forumId) => {
    if (!window.confirm('Are you sure you want to delete this forum post? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingForum(forumId);
      const response = await forumAPI.deleteForum(forumId);
      
      if (response.success) {
        fetchForums();
      } else {
        alert(response.message || 'Failed to delete forum post');
      }
    } catch (err) {
      console.error('Error deleting forum:', err);
      alert(err.data?.message || err.message || 'Failed to delete forum post');
    } finally {
      setDeletingForum(null);
    }
  };

  const handleDeleteAttachment = async (forumId, attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      setDeletingAttachment(attachmentId);
      const response = await forumAPI.deleteAttachment(forumId, attachmentId);
      
      if (response.success) {
        fetchForums();
      } else {
        alert(response.message || 'Failed to delete attachment');
      }
    } catch (err) {
      console.error('Error deleting attachment:', err);
      alert(err.data?.message || err.message || 'Failed to delete attachment');
    } finally {
      setDeletingAttachment(null);
    }
  };

  const handleReplyToForum = (forum) => {
    if (!authenticated) {
      setLoginPromptAction('reply');
      setShowLoginPrompt(true);
      return;
    }
    setReplyingToForum(forum);
    setReplyText('');
    setReplyAttachments([]);
  };

  const handleCancelReply = () => {
    setReplyingToForum(null);
    setReplyText('');
    setReplyAttachments([]);
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply');
      return;
    }

    try {
      setSubmittingReply(true);
      const formData = new FormData();
      formData.append('reply_text', replyText);
      
      // Add attachments
      replyAttachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const response = await forumAPI.replyToForumPublic(replyingToForum.id, formData);
      
      if (response.success) {
        setReplyingToForum(null);
        setReplyText('');
        setReplyAttachments([]);
        fetchForums();
      } else {
        alert(response.message || 'Failed to post reply');
      }
    } catch (err) {
      console.error('Error posting reply:', err);
      alert(err.data?.message || err.message || 'Failed to post reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    setReplyAttachments(files);
  };

  const handleVote = async (reply, value) => {
    if (!authenticated) {
      setLoginPromptAction('vote');
      setShowLoginPrompt(true);
      return;
    }
    // Optimistic update for better UX without reloading the entire list
    const previousForums = forums;
    const isUnvote = reply.user_vote === value;

    const computeUpdatedReply = (r) => {
      if (r.id !== reply.id) return r;

      const previousVote = r.user_vote || 0; // 1, -1, or 0
      let newVote = isUnvote ? 0 : value; // 1, -1, or 0
      let thumbsUp = r.thumbs_up_count || 0;
      let thumbsDown = r.thumbs_down_count || 0;

      if (isUnvote) {
        if (previousVote === 1) thumbsUp = Math.max(0, thumbsUp - 1);
        if (previousVote === -1) thumbsDown = Math.max(0, thumbsDown - 1);
      } else {
        // Switching or adding a vote
        if (previousVote === 1 && value === -1) {
          thumbsUp = Math.max(0, thumbsUp - 1);
          thumbsDown += 1;
        } else if (previousVote === -1 && value === 1) {
          thumbsDown = Math.max(0, thumbsDown - 1);
          thumbsUp += 1;
        } else if (previousVote === 0) {
          if (value === 1) thumbsUp += 1;
          if (value === -1) thumbsDown += 1;
        }
      }

      return {
        ...r,
        user_vote: newVote === 0 ? null : newVote,
        thumbs_up_count: thumbsUp,
        thumbs_down_count: thumbsDown,
      };
    };

    try {
      setVotingReplyId(reply.id);

      // Apply optimistic UI update
      setForums((prev) =>
        prev.map((forum) => ({
          ...forum,
          replies: forum.replies?.map(computeUpdatedReply),
          admin_replies: forum.admin_replies?.map(computeUpdatedReply),
        }))
      );

      // Perform API request
      if (isUnvote) {
        await forumAPI.unvoteReply(reply.id);
      } else {
        await forumAPI.voteReply(reply.id, value);
      }
    } catch (err) {
      // Rollback on error
      setForums(previousForums);
      console.error('Error voting reply:', err);
      alert(err.data?.message || err.message || 'Failed to vote on reply');
    } finally {
      setVotingReplyId(null);
    }
  };

  const handleForumVote = async (forum, value) => {
    if (!authenticated) {
      setLoginPromptAction('vote');
      setShowLoginPrompt(true);
      return;
    }
    const previousForums = forums;
    const isUnvote = forum.user_vote === value;

    const updatedForum = (f) => {
      if (f.id !== forum.id) return f;
      const previousVote = f.user_vote || 0;
      let newVote = isUnvote ? 0 : value;
      let thumbsUp = f.thumbs_up_count || 0;
      let thumbsDown = f.thumbs_down_count || 0;

      if (isUnvote) {
        if (previousVote === 1) thumbsUp = Math.max(0, thumbsUp - 1);
        if (previousVote === -1) thumbsDown = Math.max(0, thumbsDown - 1);
      } else {
        if (previousVote === 1 && value === -1) {
          thumbsUp = Math.max(0, thumbsUp - 1);
          thumbsDown += 1;
        } else if (previousVote === -1 && value === 1) {
          thumbsDown = Math.max(0, thumbsDown - 1);
          thumbsUp += 1;
        } else if (previousVote === 0) {
          if (value === 1) thumbsUp += 1;
          if (value === -1) thumbsDown += 1;
        }
      }

      return {
        ...f,
        user_vote: newVote === 0 ? null : newVote,
        thumbs_up_count: thumbsUp,
        thumbs_down_count: thumbsDown,
      };
    };

    try {
      setVotingForumId(forum.id);
      setForums((prev) => prev.map(updatedForum));
      if (isUnvote) {
        await forumAPI.unvoteForum(forum.id);
      } else {
        await forumAPI.voteForum(forum.id, value);
      }
    } catch (err) {
      setForums(previousForums);
      console.error('Error voting forum:', err);
      alert(err.data?.message || err.message || 'Failed to vote on question');
    } finally {
      setVotingForumId(null);
    }
  };

  if (loading) {
    return (
      <div className="forum-list-container">
        <div className="loading">Loading forums...</div>
      </div>
    );
  }

  return (
    <div className="forum-list-container">
      <div className="forum-header">
        <h1>Forum Questions</h1>
        <button 
          className="btn btn-primary"
          onClick={() => {
            if (!authenticated) {
              setLoginPromptAction('create');
              setShowLoginPrompt(true);
              return;
            }
            setShowCreateForm(true);
          }}
        >
          Ask a Question
        </button>
      </div>

      <div className="forum-filters">
        <div className="filters-left">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Questions</option>
            <option value="open">Open</option>
            <option value="completed">Completed</option>
            <option value="spam">Spam</option>
            <option value="removed">Removed</option>
          </select>
        </div>
        <div className="filters-right">
          <div className="search-input">
            <i className="bi bi-search search-icon"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, description, or user..."
              className="filter-input"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="forum-list">
        {forums.length === 0 ? (
          <div className="no-forums">
            <p>No forum questions found.</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              Ask Your First Question
            </button>
          </div>
        ) : (
          forums.map((forum) => (
            <div key={forum.id} className="forum-item">
              <div className="forum-item-header">
                <div className="forum-title-section">
                  <h3 className="forum-title">
                    {forum.title || 'No Title'}
                  </h3>
                  <div className="forum-author">
                    <i className="bi bi-person-circle"></i>
                    <span>Posted by {forum.user?.name || 'Unknown User'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {getStatusBadge(forum.status)}
                  {forum.user_id === user?.id && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEditForum(forum)}
                        className="btn btn-sm"
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.875rem',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          border: '1px solid #d1d5db'
                        }}
                        title="Edit this forum post"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteForum(forum.id)}
                        className="btn btn-sm"
                        disabled={deletingForum === forum.id}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.875rem',
                          backgroundColor: deletingForum === forum.id ? '#fee2e2' : '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fecaca'
                        }}
                        title="Delete this forum post"
                      >
                        {deletingForum === forum.id ? (
                          <i className="bi bi-hourglass-split"></i>
                        ) : (
                          <i className="bi bi-trash"></i>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="forum-description">
                {renderTextWithSeeMore(
                  forum.description,
                  forum.id,
                  expandedDescriptions.has(forum.id),
                  () => toggleDescription(forum.id)
                )}
              </div>

              

              {forum.attachments && forum.attachments.length > 0 && (
                <div className="forum-attachments">
                  <h4>Attachments:</h4>
                  <div className="attachment-list">
                    {forum.attachments.map((attachment, index) => {
                      const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                      const canDelete = forum.user_id === user?.id;
                      return (
                        <div key={attachment.id || index} className="attachment-item" style={{ position: 'relative' }}>
                          {isImage ? (
                            <div className="image-attachment" style={{ position: 'relative' }}>
                              <img 
                                src={attachment.url} 
                                alt={`Attachment ${index + 1}`}
                                className="attachment-image"
                                onLoad={() => console.log('✅ Image loaded successfully:', attachment.url)}
                                onError={(e) => {
                                  console.log('❌ Image failed to load:', attachment.url);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteAttachment(forum.id, attachment.id)}
                                  disabled={deletingAttachment === attachment.id}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    background: 'rgba(220, 38, 38, 0.9)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    zIndex: 10
                                  }}
                                  title="Delete this attachment"
                                >
                                  {deletingAttachment === attachment.id ? (
                                    <i className="bi bi-hourglass-split"></i>
                                  ) : (
                                    <i className="bi bi-trash"></i>
                                  )}
                                </button>
                              )}
                              <a 
                                href={attachment.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="attachment-link"
                                style={{display: 'none'}}
                              >
                                View Image {index + 1}
                              </a>
                            </div>
                          ) : (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <a 
                                href={attachment.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="attachment-link"
                              >
                                {attachment.original_name || `Attachment ${index + 1}`}
                              </a>
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteAttachment(forum.id, attachment.id)}
                                  disabled={deletingAttachment === attachment.id}
                                  style={{
                                    marginLeft: '8px',
                                    padding: '4px 8px',
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    border: '1px solid #fecaca',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    cursor: 'pointer'
                                  }}
                                  title="Delete this attachment"
                                >
                                  {deletingAttachment === attachment.id ? (
                                    <i className="bi bi-hourglass-split"></i>
                                  ) : (
                                    <i className="bi bi-trash"></i>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Question vote actions - visible only when voting is enabled */}
              {globalVotingEnabled && forum.voting_enabled !== false && (
                <div className="vote-actions">
                  <button
                    className={`icon-button like ${forum.user_vote === 1 ? 'active' : ''}`}
                    onClick={() => handleForumVote(forum, 1)}
                    disabled={!!votingForumId}
                    title={authenticated ? "Like" : "Log in to like"}
                  >
                    <i className={forum.user_vote === 1 ? 'bi bi-hand-thumbs-up-fill' : 'bi bi-hand-thumbs-up'}></i>
                    <span className="count">{forum.thumbs_up_count || 0}</span>
                  </button>
                  <button
                    className={`icon-button dislike ${forum.user_vote === -1 ? 'active' : ''}`}
                    onClick={() => handleForumVote(forum, -1)}
                    disabled={!!votingForumId}
                    title={authenticated ? "Dislike" : "Log in to dislike"}
                  >
                    <i className={forum.user_vote === -1 ? 'bi bi-hand-thumbs-down-fill' : 'bi bi-hand-thumbs-down'}></i>
                    <span className="count">{forum.thumbs_down_count || 0}</span>
                  </button>
                </div>
              )}

              {/* Public Replies */}
              {forum.replies && forum.replies.filter(reply => !reply.is_admin_reply).length > 0 && (
                <div className="public-replies">
                  <h4>Replies ({forum.replies.filter(reply => !reply.is_admin_reply).length}):</h4>
                  {forum.replies.filter(reply => !reply.is_admin_reply).map((reply, index) => (
                    <div key={index} className="public-reply">
                      <div className="reply-header">
                        <div className="reply-author">
                          <i className="bi bi-person-circle"></i>
                          <strong>{reply.user?.name || 'Unknown User'}</strong>
                        </div>
                        <span className="reply-date">
                          {formatDate(reply.created_at)}
                        </span>
                      </div>
                      <div className="reply-content">
                        {renderTextWithSeeMore(
                          reply.reply_text,
                          reply.id,
                          expandedReplies.has(reply.id),
                          () => toggleReply(reply.id)
                        )}
                        {reply.attachments && reply.attachments.length > 0 && (
                          <div className="reply-attachments">
                            {reply.attachments.map((attachment, attIndex) => {
                              const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                              return (
                                <div key={attIndex} className="attachment-item">
                                  {isImage ? (
                                    <div className="image-attachment">
                                      <img 
                                        src={attachment.url} 
                                        alt={`Reply Attachment ${attIndex + 1}`}
                                        className="attachment-image"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'block';
                                        }}
                                      />
                                      <a 
                                        href={attachment.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="attachment-link"
                                        style={{display: 'none'}}
                                      >
                                        View Image {attIndex + 1}
                                      </a>
                                    </div>
                                  ) : (
                                    <a 
                                      href={attachment.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      {attachment.original_name || `Attachment ${attIndex + 1}`}
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Vote actions - visible only when voting is enabled on forum */}
                        {globalVotingEnabled && forum.voting_enabled !== false && (
                          <div className="vote-actions" style={{ marginTop: '8px' }}>
                            <button
                              className={`icon-button like ${reply.user_vote === 1 ? 'active' : ''}`}
                              onClick={() => handleVote(reply, 1)}
                              disabled={!!votingReplyId}
                              title={authenticated ? "Like" : "Log in to like"}
                            >
                              <i className={reply.user_vote === 1 ? 'bi bi-hand-thumbs-up-fill' : 'bi bi-hand-thumbs-up'}></i>
                              <span className="count">{reply.thumbs_up_count || 0}</span>
                            </button>
                            <button
                              className={`icon-button dislike ${reply.user_vote === -1 ? 'active' : ''}`}
                              onClick={() => handleVote(reply, -1)}
                              disabled={!!votingReplyId}
                              title={authenticated ? "Dislike" : "Log in to dislike"}
                            >
                              <i className={reply.user_vote === -1 ? 'bi bi-hand-thumbs-down-fill' : 'bi bi-hand-thumbs-down'}></i>
                              <span className="count">{reply.thumbs_down_count || 0}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Admin Replies */}
              {forum.admin_replies && forum.admin_replies.length > 0 && (
                <div className="admin-replies">
                  <h4>Admin Replies:</h4>
                  {forum.admin_replies.map((reply, index) => (
                    <div key={index} className="admin-reply">
                      <div className="reply-header">
                        <strong>Admin Reply</strong>
                        <span className="reply-date">
                          {formatDate(reply.created_at)}
                        </span>
                      </div>
                      <div className="reply-content">
                        {renderTextWithSeeMore(
                          reply.reply_text,
                          `admin-${reply.id}`,
                          expandedReplies.has(`admin-${reply.id}`),
                          () => toggleReply(`admin-${reply.id}`)
                        )}
                        {reply.attachments && reply.attachments.length > 0 && (
                          <div className="reply-attachments">
                            {reply.attachments.map((attachment, attIndex) => {
                              const isImage = attachment.mime_type && attachment.mime_type.startsWith('image/');
                              return (
                                <div key={attIndex} className="attachment-item">
                                  {isImage ? (
                                    <div className="image-attachment">
                                      <img 
                                        src={attachment.url} 
                                        alt={`Admin Attachment ${attIndex + 1}`}
                                        className="attachment-image"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'block';
                                        }}
                                      />
                                      <a 
                                        href={attachment.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="attachment-link"
                                        style={{display: 'none'}}
                                      >
                                        View Admin Image {attIndex + 1}
                                      </a>
                                    </div>
                                  ) : (
                                    <a 
                                      href={attachment.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      {attachment.original_name || `Admin Attachment ${attIndex + 1}`}
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Vote actions for admin reply - only when forum voting is enabled */}
                        {globalVotingEnabled && forum.voting_enabled !== false && (
                          <div className="vote-actions" style={{ marginTop: '8px' }}>
                            <button
                              className={`icon-button like ${reply.user_vote === 1 ? 'active' : ''}`}
                              onClick={() => handleVote(reply, 1)}
                              disabled={!!votingReplyId}
                              title={authenticated ? "Like" : "Log in to like"}
                            >
                              <i className={reply.user_vote === 1 ? 'bi bi-hand-thumbs-up-fill' : 'bi bi-hand-thumbs-up'}></i>
                              <span className="count">{reply.thumbs_up_count || 0}</span>
                            </button>
                            <button
                              className={`icon-button dislike ${reply.user_vote === -1 ? 'active' : ''}`}
                              onClick={() => handleVote(reply, -1)}
                              disabled={!!votingReplyId}
                              title={authenticated ? "Dislike" : "Log in to dislike"}
                            >
                              <i className={reply.user_vote === -1 ? 'bi bi-hand-thumbs-down-fill' : 'bi bi-hand-thumbs-down'}></i>
                              <span className="count">{reply.thumbs_down_count || 0}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              {forum.commenting_enabled && (
                <div className="reply-section">
                  {replyingToForum?.id === forum.id ? (
                    <div className="reply-form">
                      <h4>Post a Reply</h4>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply here..."
                        rows={4}
                        className="reply-textarea"
                      />
                      <div className="attachment-upload">
                        <label htmlFor={`reply-attachments-${forum.id}`} className="attachment-label">
                          <i className="bi bi-paperclip"></i>
                          Attach Files ({replyAttachments.length} selected)
                        </label>
                        <input
                          id={`reply-attachments-${forum.id}`}
                          type="file"
                          multiple
                          onChange={handleAttachmentChange}
                          className="attachment-input"
                          accept="image/*,.pdf,.doc,.docx,.txt"
                        />
                      </div>
                      <div className="reply-actions">
                        <button
                          onClick={handleReplySubmit}
                          disabled={submittingReply || !replyText.trim()}
                          className="btn btn-primary"
                        >
                          {submittingReply ? 'Posting...' : 'Post Reply'}
                        </button>
                        <button
                          onClick={handleCancelReply}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleReplyToForum(forum)}
                      className="btn btn-outline-primary"
                      title={authenticated ? "Reply to this post" : "Log in to reply"}
                    >
                      <i className="bi bi-reply"></i>
                      Reply to this post
                    </button>
                  )}
                </div>
              )}

              {!forum.commenting_enabled && (
                <div className="commenting-disabled">
                  <i className="bi bi-lock"></i>
                  <span>Commenting is disabled for this post</span>
                </div>
              )}

              <div className="forum-meta">
                <span className="forum-date">
                  Asked on {formatDate(forum.created_at)}
                </span>
                <span className="forum-replies">
                  {forum.replies?.filter(reply => !reply.is_admin_reply).length || 0} replies, {forum.admin_replies?.length || 0} admin replies
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateForm && (
        <ForumCreateForm 
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            fetchForums();
          }}
        />
      )}

      {editingForum && (
        <ForumEditForm
          forum={editingForum}
          onClose={() => setEditingForum(null)}
          onSuccess={() => {
            setEditingForum(null);
            fetchForums();
          }}
        />
      )}

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        action={loginPromptAction}
      />
    </div>
  );
};

export default ForumList;
